import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { ExtraditionMlaWizardDraft } from '../../../types/extraditionMlaCaseRequest';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import { exceedsMax, isTodayOrFutureIsoDate, maxCharLimitExceededError, todayIsoDateLocal } from '../../../utils/caseFieldValidation';
import { validationDateFutureOnly } from '../../../utils/validationI18n';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { ExtraditionMlaWizardActions } from './ExtraditionMlaWizardActions';
import { extraditionMlaWizardPath, ExtraditionMlaWizardShell, FieldError, inpClass } from './ExtraditionMlaWizardShell';
import { isHighUrgencyLevel } from './extraditionMlaHelpers';
import { useExtraditionMlaLookups } from './useExtraditionMlaLookups';
import { validateExtraditionMlaStep } from './extraditionMlaStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

export default function ExtraditionMlaStep4TimelinePage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useExtraditionMlaWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { urgencyLevels, loading } = useExtraditionMlaLookups('step4');
  const minDeadlineDate = useMemo(() => todayIsoDateLocal(), []);
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateExtraditionMlaStep(4, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(extraditionMlaWizardPath(5, draft.draftRequestId));
  };

  return (
    <ExtraditionMlaWizardShell currentStep={4} draft={draft}>
      <SectionCard title={f.sectionTimeline}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.urgencyLevel} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-urgency"
              hasError={Boolean(errors.urgencyLevelId)}
              value={draft.urgencyLevelId}
              disabled={loading}
              placeholder={f.selectUrgencyLevel}
              options={toOpts(urgencyLevels)}
              onChange={(id) => {
                const name = urgencyLevels.find((u) => u.id === id)?.name ?? '';
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  urgencyLevelId: id,
                  urgencyLevelName: name }));
                if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                clearErr('urgencyLevelId');
              }}
            />
            <FieldError message={errors.urgencyLevelId} />
          </div>
          <div className="ccr-fg">
            <label>{f.deadlineIfAny}</label>
            <FormattedDateInput
              id="emla-deadline"
              className={inpClass(Boolean(errors.deadline))}
              value={draft.deadline}
              minDate={minDeadlineDate}
              enableYmdViews
              onChange={(next) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({ ...d, deadline: next }));
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, deadline: validationDateFutureOnly() }));
                } else {
                  clearErr('deadline');
                }
              }}
            />
            <FieldError message={errors.deadline} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>
            {f.urgencyJustification} {urgencyJustificationRequired ? <span className="req">*</span> : null}
          </label>
          <textarea
            id="emla-urgencyJustification"
            className={inpClass(Boolean(errors.urgencyJustification))}
            rows={4}
            value={draft.urgencyJustification}
            onChange={(e) => {
              setDraft((d: ExtraditionMlaWizardDraft) => ({
                ...d,
                urgencyJustification: maxLen('urgencyJustification', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.urgencyJustificationReason}
          />
          <FieldError message={errors.urgencyJustification} />
        </div>
      </SectionCard>

      <ExtraditionMlaWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={4}
        onBack={() => nav(extraditionMlaWizardPath(3, draft.draftRequestId))}
        onNext={next}
        validateBeforeSave={validate}
      />
    </ExtraditionMlaWizardShell>
  );
}
